import * as Sentry from '@sentry/react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import React, { ErrorInfo } from 'react';
import { shouldSuppressError } from '@utils/errorUtils';
import Logo from '@components/logo/Logo';

interface Props {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps> | React.ReactElement;
}

const ErrorFallback: React.FC<FallbackProps> = ({ resetErrorBoundary }) => {
  return (
    <div role="alert" className="error-boundary-container bg-primary-0">
      <div className="error-boundary-content">
        <Logo className="error-boundary-logo" />
        <h1 className="error-boundary-heading">Something went wrong</h1>
        <p className="error-boundary-message">
          We encountered an unexpected error. Please try again.
        </p>
        <button
          type="button"
          onClick={resetErrorBoundary}
          className="error-boundary-button"
        >
          Try again
        </button>
      </div>
    </div>
  );
};

export const AppErrorBoundary: React.FC<Props> = ({ children, fallback }) => {
  // Check if fallback is a React element or a component
  const isReactElement = React.isValidElement(fallback);
  const fallbackProps = isReactElement
    ? { fallback }
    : {
        FallbackComponent:
          (fallback as React.ComponentType<FallbackProps>) || ErrorFallback,
      };

  return (
    <ErrorBoundary
      {...fallbackProps}
      onError={(error: Error, info: ErrorInfo) => {
        // Only send to Sentry if it's not a suppressed error (e.g., EDC404)
        if (!shouldSuppressError(error)) {
          Sentry.captureException(error, {
            tags: { module: 'lms-self-checkin' },
            level: 'error',
            contexts: {
              react: {
                componentStack: info.componentStack,
              },
            },
          });
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default AppErrorBoundary;
